import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectUser } from "@/apps/admin/features/authentication/authenticationSlice";
import {
  addNotification,
  serverErrorNotification,
  SEVERITY,
} from "@/apps/admin/features/notifications/notificationSlice";
import { useAdminDispatch } from "@/apps/admin/hooks/useAdminDispatch";
import type {
  AppFeatureFlags,
  AppPlatform,
  AppScope,
  AppUser,
  Notifier,
} from "@/common/appContext/types";
import { failureText } from "@/state/api/requestFailure";

/**
 * Admin's platform (GEN5-5269 ARCHITECTURE item 4). Admin acts in the admin
 * scope; a page that acts on one organization sets `{ kind: "admin",
 * organizationId }` with `AppScopeProvider`. Its notifications are verbose
 * (AD1, D3).
 */

const ADMIN_SCOPE: AppScope = { kind: "admin" };
const useAdminScope = (): AppScope => ADMIN_SCOPE;

/**
 * No organization's product features are on in admin: before, the flags
 * read the route's organization, which admin's routes never have, so every
 * flag read "off" by accident; now it is said, and ready (not loading, not
 * an error). Admin's own gates are its permissions and capabilities.
 */
const NO_FLAGS: AppFeatureFlags = {
  featureFlags: [],
  isLoading: false,
  isFetching: false,
  error: undefined,
  refetch: () => undefined,
};
const useAdminFeatureFlags = (): AppFeatureFlags => NO_FLAGS;

const useAdminUser = (): AppUser | null => {
  const user = useSelector(selectUser);
  const email = user?.email ?? null;
  const uid = user?.uid;
  return useMemo(
    () => (email === null || uid === undefined ? null : { email, uid }),
    [email, uid]
  );
};

const useVerboseNotifier = (): Notifier => {
  const dispatch = useAdminDispatch();
  return useMemo(
    () => ({
      verbosity: "verbose",
      success: (message) =>
        dispatch(addNotification({ message, severity: SEVERITY.SUCCESS })),
      info: (message) =>
        dispatch(addNotification({ message, severity: SEVERITY.INFO })),
      error: (failure) =>
        dispatch(serverErrorNotification(failureText(failure, "verbose"))),
    }),
    [dispatch]
  );
};

export const ADMIN_PLATFORM: AppPlatform = {
  app: "admin",
  useUser: useAdminUser,
  useScope: useAdminScope,
  useFeatureFlags: useAdminFeatureFlags,
  useNotifier: useVerboseNotifier,
  // Admin has no record pages of its own for a stakeholder.
  links: { stakeholder: () => null },
  capabilities: {},
};
